import React from "react";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { Header, Footer } from "../components/common";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-950" data-testid="terms-page">
      <Header />
      
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Terms & <span className="gradient-text">Conditions</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-8 space-y-8"
          >
            {/* Key Requirements */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Key Requirements</h2>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Provide safe, legal parking space</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Allow sufficient space around car (approx. 1m)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Provide access to power (if required)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Provide access to water (if required)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Ensure vehicle is unlocked at arrival</span>
                </li>
              </ul>
              <p className="text-amber-400 text-sm mt-4 bg-amber-400/10 p-3 rounded-lg border border-amber-400/20">
                <strong>⚠️ Please note:</strong> If you do choose for your keys to be left at a designated location, we are not liable if they go missing afterwards.
              </p>
            </div>

            {/* Before Service */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Before Service</h2>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Remove all personal belongings from interior & boot</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Remove all valuables</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Booking deposit required ($20)</span>
                </li>
              </ul>
              <p className="text-sm text-slate-500 mt-4">
                *We are not responsible for personal items left inside the vehicle
              </p>
            </div>

            {/* Cancellation Policy */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Cancellation Policy</h2>
              <ul className="space-y-2 text-slate-300">
                <li>• <span className="text-green-400">48+ hours:</span> Free reschedule</li>
                <li>• <span className="text-yellow-400">24-48 hours:</span> Deposit retained</li>
                <li>• <span className="text-red-400">Under 24 hours:</span> 50% service fee may apply</li>
              </ul>
            </div>

            {/* Condition Disclosure */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Condition Disclosure</h2>
              <p className="text-slate-300 mb-3">Customers must accurately declare:</p>
              <ul className="space-y-1 text-slate-400">
                <li>• Excessive pet hair, sand/mud buildup</li>
                <li>• Mold, biohazard, vomit, bodily fluids</li>
                <li>• Heavy stains, smoke odor</li>
              </ul>
              <p className="text-cyan-400 font-medium mt-3">
                Undisclosed heavy conditions may result in additional charges or rescheduling.
              </p>
              <p className="text-slate-400 mt-3">
                <span className="text-cyan-400 font-medium">Unsure about your car's condition?</span> You can upload photos during checkout or contact us via our <a href="/contact" className="text-cyan-400 underline hover:text-cyan-300">contact page</a> and we will get back to you with an accurate quote.
              </p>
            </div>

            {/* Payment Policy */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Payment Policy</h2>
              <ul className="space-y-2 text-slate-300">
                <li>• Payment due immediately after service</li>
                <li>• Card & cash accepted</li>
                <li>• No "pay later" options</li>
                <li>• $20 deposit required to secure booking</li>
                <li>• <span className="text-yellow-400 font-medium">Deposits are non-refundable</span></li>
                <li>• <span className="text-cyan-400">Deposits are transferable with 48 hours notice</span></li>
              </ul>
              <p className="mt-4 text-slate-300 bg-slate-800/50 p-3 rounded-lg">
                <span className="text-cyan-400 font-medium">Unsure about your booking?</span> Text, call or email us and we will respond same day.
              </p>
            </div>

            {/* Weather & Safety */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Weather & Safety</h2>
              <p className="text-slate-300 mb-3">
                Service may require rescheduling for safety and quality reasons due to weather.
              </p>
              <p className="text-cyan-400 mb-3">
                In case of rain or thunderstorm, we may be able to do service if there is shade that protects from rain.
              </p>
              <p className="text-slate-300 mb-2">We will refuse service if:</p>
              <ul className="space-y-1 text-slate-400">
                <li>• Unsafe location</li>
                <li>• Aggressive behaviour</li>
                <li>• Hazardous materials</li>
                <li>• Undeclared biohazard contamination</li>
              </ul>
            </div>

            {/* Satisfaction Guarantee */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Satisfaction Guarantee</h2>
              <p className="text-slate-300">
                If you are not fully satisfied, notify us on site and we will address concerns immediately.
              </p>
              <p className="text-slate-300 mt-3">
                We are not responsible for personal items left inside the vehicle.
              </p>
              <p className="text-slate-300 mt-2">
                We are not responsible for pre-existing damage, worn trim, loose panels or fragile components.
              </p>
            </div>

            {/* Photo & Media Consent */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Photo & Media Consent</h2>
              <p className="text-slate-300">
                We may take before and after photos for quality control and marketing.
              </p>
              <p className="text-cyan-400 mt-2">
                License plates and personal details will not be displayed without permission.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
